import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ArrowRight, CheckCircle, Clock, Sparkles, Zap } from 'lucide-react';
import { ConversationalInput } from '@/components/shared/ConversationalInput';
import { QuickPrompts } from '@/components/shared/QuickPrompts';
import { useToast } from '@/components/ui/use-toast';

interface PowerMove {
  id: string;
  title: string;
  description: string;
  prompt: string;
  placeholder: string;
  estimatedMinutes: number;
  content: string;
  completed: boolean;
  category: 'strategic' | 'networking' | 'skill-building' | 'visibility';
}

const powerMoves: PowerMove[] = [
  {
    id: 'strategic-initiative',
    title: 'Strategic Initiative',
    description: 'A high-impact project that showcases your strategic thinking.',
    prompt: 'What strategic project could you lead that would create significant business value?',
    placeholder: 'Describe a strategic initiative you could own...',
    estimatedMinutes: 2,
    content: '',
    completed: false,
    category: 'strategic'
  },
  {
    id: 'network-expansion',
    title: 'Network Expansion',
    description: 'Targeted relationship building for career acceleration.',
    prompt: 'Who are 3 key people you should connect with to advance your career?',
    placeholder: 'List key people and how you\'ll add value to them...',
    estimatedMinutes: 2,
    content: '',
    completed: false,
    category: 'networking'
  },
  {
    id: 'skill-development',
    title: 'Skill Development',
    description: 'Strategic skill acquisition for future opportunities.',
    prompt: 'What skill, if mastered, would make you significantly more valuable?',
    placeholder: 'Identify a high-value skill and your learning plan...',
    estimatedMinutes: 1,
    content: '',
    completed: false,
    category: 'skill-building'
  },
  {
    id: 'visibility-boost',
    title: 'Visibility Boost',
    description: 'Actions to increase your professional visibility and influence.',
    prompt: 'How can you increase your visibility within your organization or industry?',
    placeholder: 'Describe visibility-building activities...',
    estimatedMinutes: 1,
    content: '',
    completed: false,
    category: 'visibility'
  }
];

export const ConversationalPowerMoves: React.FC = () => {
  const [currentMoveIndex, setCurrentMoveIndex] = useState(0);
  const [movesData, setMovesData] = useState(powerMoves);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!startTime) {
      setStartTime(new Date());
    }
  }, []);

  const currentMove = movesData[currentMoveIndex];
  const completedMoves = movesData.filter(m => m.completed).length;
  const progress = (completedMoves / movesData.length) * 100;
  const totalEstimatedTime = movesData.reduce((sum, m) => sum + m.estimatedMinutes, 0);
  const timeSpent = startTime ? Math.round((new Date().getTime() - startTime.getTime()) / 60000) : 0;

  const categoryEmojis = {
    strategic: '⚡',
    networking: '🤝',
    'skill-building': '📚',
    visibility: '👁️'
  };

  const handleContentChange = (content: string) => {
    const updatedMoves = [...movesData];
    updatedMoves[currentMoveIndex] = {
      ...currentMove,
      content,
      completed: content.trim().length > 20
    };
    setMovesData(updatedMoves);
  };

  const handleNext = () => {
    if (currentMove.content.trim()) {
      if (currentMoveIndex < movesData.length - 1) {
        setCurrentMoveIndex(currentMoveIndex + 1);
      } else {
        toast({
          title: "Power Moves™ Complete! ⚡",
          description: `You've defined your strategic career moves in ${timeSpent} minutes.`,
        });
      }
    }
  };

  const handlePrevious = () => {
    if (currentMoveIndex > 0) {
      setCurrentMoveIndex(currentMoveIndex - 1);
    }
  };

  const handleQuickPrompt = (prompt: string) => {
    console.log('Quick prompt selected:', prompt);
  };

  const isLastMove = currentMoveIndex === movesData.length - 1;
  const canProceed = currentMove.content.trim().length > 0;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header with Progress */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent flex items-center gap-2">
              <Zap className="h-6 w-6 text-primary" />
              Power Moves™
            </h1>
            <p className="text-muted-foreground">Strategic actions that accelerate your career</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-muted-foreground">
              {completedMoves} of {movesData.length} moves defined
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>{timeSpent}m / ~{totalEstimatedTime}m</span>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Overall Progress</span>
            <span className="font-medium">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Move Navigation */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {movesData.map((move, index) => (
            <Button
              key={move.id}
              variant={index === currentMoveIndex ? "default" : move.completed ? "secondary" : "outline"}
              size="sm"
              onClick={() => setCurrentMoveIndex(index)}
              className="whitespace-nowrap flex items-center gap-2"
            >
              {move.completed && <CheckCircle className="h-3 w-3" />}
              <span className="text-xs">{move.title}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Current Move */}
      <div className="space-y-6">
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <span className="text-lg">{categoryEmojis[currentMove.category]}</span>
                  {currentMove.title}
                </h2>
                <p className="text-muted-foreground">{currentMove.description}</p>
              </div>
              <Badge variant="secondary" className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                ~{currentMove.estimatedMinutes}m
              </Badge>
            </div>
          </div>
        </Card>

        {/* Quick Prompts */}
        <QuickPrompts
          artifactType="power-moves"
          sectionType={currentMove.category}
          onPromptSelect={handleQuickPrompt}
        />

        {/* Conversational Input */}
        <ConversationalInput
          question={currentMove.prompt}
          placeholder={currentMove.placeholder}
          value={currentMove.content}
          onChange={handleContentChange}
          artifactType="power-moves"
          sectionType={currentMove.category}
          userContext={`Move category: ${currentMove.category}. Completed moves: ${completedMoves}`}
        />

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentMoveIndex === 0}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Previous
          </Button>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Sparkles className="h-4 w-4" />
            <span>Move {currentMoveIndex + 1} of {movesData.length}</span>
          </div>

          <Button
            onClick={handleNext}
            disabled={!canProceed}
            className="flex items-center gap-2"
          >
            {isLastMove ? 'Complete Strategy' : 'Next Move'}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        {!canProceed && (
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Define this power move to continue to the next one
            </p>
          </div>
        )}
      </div>
    </div>
  );
};